import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Layers, Plus, Search, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import AppShell from "@/components/layout/AppShell";
import ProjectCard from "@/components/dashboard/ProjectCard";
import { pubState } from "@/components/dashboard/SiteInfo";
import { SiteBadge } from "@/components/sites/Badges";
import ShareDialog from "@/components/common/ShareDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import DuplicateDialog from "@/components/duplicate/DuplicateDialog";
import SaveTemplateDialog from "@/components/templates/SaveTemplateDialog";
import TemplatesGrid from "@/components/templates/TemplatesGrid";
import { STATUSES } from "@/lib/constants";
import { downloadFile, errMsg, useApi } from "@/lib/api";

const SORTS = [
  ["updated_desc", "Ultima modifica"], ["created_desc", "Più recenti"], ["created_asc", "Meno recenti"],
  ["name_asc", "Nome progetto (A-Z)"], ["company_asc", "Azienda (A-Z)"],
];

export default function Dashboard() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("updated_desc");
  const [sharing, setSharing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [view, setView] = useState("projects");
  const [dup, setDup] = useState(null);
  const [savingTpl, setSavingTpl] = useState(null);
  const [tplCount, setTplCount] = useState(null);
  const [sites, setSites] = useState(null);
  const [pub, setPub] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/projects", { params: { q, status, sort } });
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); setItems([]); }
  }, [api, q, status, sort]);

  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { api.get("/sites", { params: { include_archived: true } }).then(({ data }) => setSites(data)).catch(() => setSites([])); }, [api]);
  const byProject = {};  // the active site of each project (else its latest archived one)
  (sites || []).forEach((x) => { const c = byProject[x.project_id]; if (!c || (x.active && !c.active) || (x.active === c.active && x.updated_at > c.updated_at)) byProject[x.project_id] = x; });
  const counts = { all: 0, draft: 0, published: 0, archived: 0 };
  (items || []).forEach((p) => { counts.all += 1; counts[pubState(p, byProject[p.id])] += 1; });
  const shown = (items || []).filter((p) => pub === "all" || pubState(p, byProject[p.id]) === pub);
  const orphans = (sites || []).filter((x) => x.project.deleted && x.active && !q && (pub === "all" || pub === (["online", "sospeso"].includes(x.status) ? "published" : "draft")));
  useEffect(() => {
    if (!items?.some((p) => p.generation?.status === "running")) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [items, load]);

  const patch = (id, upd) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...upd } : x)));
  const onStatus = async (p, s) => {
    try { await api.patch(`/projects/${p.id}`, { status: s }); patch(p.id, { status: s }); toast.success("Stato aggiornato"); } catch (e) { toast.error(errMsg(e)); }
  };
  const onDelete = async () => {
    const p = deleting; setDeleting(null);
    try {
      await api.delete(`/projects/${p.id}`);
      setItems((xs) => xs.filter((x) => x.id !== p.id));
      toast.success(`"${p.name}" spostato nel cestino`, { action: { label: "Annulla", onClick: async () => { await api.post(`/trash/${p.id}/restore`); load(); } } });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onExport = async (p) => {
    const t = toast.loading("Preparazione del file ZIP…");
    try { await downloadFile(api, `/projects/${p.id}/export`, "progetto.zip"); toast.success("Esportazione completata", { id: t }); }
    catch (e) { toast.error(errMsg(e), { id: t }); }
  };

  return (
    <AppShell>
      <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-[1500px]">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-sky-700 font-medium">Siti web</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{view === "projects" ? "Siti web" : "Template"}</h1>
            <p className="mt-2 text-sm text-slate-500">{items ? `${items.length} siti` : "Caricamento…"} · editor, anteprima, pubblicazione, cliente e dominio nello stesso posto · <Link to="/sites" className="text-sky-700 hover:underline" data-testid="dashboard-builds-link">Compilazioni e operazioni multiple</Link> · <Link to="/backup" className="text-sky-700 hover:underline" data-testid="dashboard-trash-link">Backup e cestino</Link></p>
          </div>
          <Button asChild className="bg-sky-600 hover:bg-sky-700 self-start sm:self-auto" data-testid="new-project-btn">
            <Link to="/new"><Plus size={16} className="mr-1.5" />Nuovo sito</Link>
          </Button>
        </div>

        <div className="mt-6 inline-flex rounded-lg border bg-white p-1" role="tablist" data-testid="dashboard-view-tabs">
          {[["projects", "Siti"], ["templates", `Template${tplCount ? ` (${tplCount})` : ""}`]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)} data-testid={`dashboard-tab-${k}`}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${view === k ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
          ))}
        </div>
        {view === "templates" ? <TemplatesGrid onUse={(t) => setDup({ ...t, is_template: true })} onCount={setTplCount} /> : <>
        <div className="mt-6 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per azienda, progetto o settore" className="pl-9 bg-white" data-testid="project-search-input" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 lg:pb-0" data-testid="pub-filters">
            {[["all", "Tutti"], ["draft", "Bozze"], ["published", "Pubblicati"], ["archived", "Archiviati"]].map(([k, label]) => (
              <button key={k} onClick={() => setPub(k)} data-testid={`pub-filter-${k}`}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs border transition-colors ${pub === k ? "bg-navy-900 text-white border-navy-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{label}{items && sites ? ` (${counts[k]})` : ""}</button>
            ))}
          </div>
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full lg:w-48 bg-white" data-testid="project-workflow-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Ogni fase di lavoro</SelectItem>{Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-full lg:w-52 bg-white lg:ml-auto" data-testid="project-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="project-grid">
          {items === null && [0, 1, 2].map((i) => <Skeleton key={i} className="h-80 rounded-xl" />)}
          {shown.map((p, i) => (
            <ProjectCard key={p.id} p={p} s={byProject[p.id]} index={i} onStatus={onStatus} onShare={setSharing} onDuplicate={setDup} onSaveTemplate={setSavingTpl} onDelete={setDeleting} onExport={onExport} />
          ))}
        </div>
        {orphans.length > 0 && (
          <section className="mt-8 rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="orphan-sites">
            <p className="text-sm font-medium text-slate-800">Siti con progetto eliminato ({orphans.length})</p>
            <p className="text-xs text-slate-500">Restano come sono: gestiscili qui o ripristina il progetto dal cestino.</p>
            <ul className="mt-2 divide-y divide-amber-100">{orphans.map((x) => (
              <li key={x.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`orphan-site-${x.id}`}><SiteBadge status={x.status} /><span className="font-medium">{x.name}</span><span className="text-xs text-slate-500">{x.client.name}</span>
                {x.urls.live && <a href={x.urls.live} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline">Online<ExternalLink size={11} /></a>}
                <Link to={`/sites/${x.id}`} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"><Layers size={12} />Gestisci</Link></li>))}</ul>
          </section>)}
        {items?.length > 0 && !shown.length && <p className="mt-6 text-sm text-slate-500" data-testid="pub-empty">Nessun sito in questo filtro.</p>}
        {items?.length === 0 && (
          <div className="mt-6 border border-dashed border-slate-300 rounded-xl p-12 text-center bg-white" data-testid="empty-projects">
            <FolderOpen className="mx-auto text-slate-400" />
            <p className="mt-3 font-medium text-slate-800">{q || status ? "Nessun progetto corrisponde ai filtri" : "Nessun progetto ancora"}</p>
            <p className="mt-1 text-sm text-slate-500">Crea la prima demo partendo da un brief.</p>
            <Button asChild className="mt-5 bg-sky-600 hover:bg-sky-700"><Link to="/new">Nuovo progetto</Link></Button>
          </div>
        )}
        </>}
      </div>
      <DuplicateDialog source={dup} open={!!dup} onOpenChange={(o) => !o && setDup(null)} />
      <SaveTemplateDialog project={savingTpl} open={!!savingTpl} onOpenChange={(o) => !o && setSavingTpl(null)} onSaved={() => setTplCount((n) => (n || 0) + 1)} />
      <ShareDialog project={sharing} open={!!sharing} onOpenChange={(o) => !o && setSharing(null)}
        onChange={(upd) => { patch(sharing.id, upd); setSharing((s) => ({ ...s, ...upd })); }} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} destructive confirmLabel="Sposta nel cestino"
        title={`Spostare "${deleting?.name}" nel cestino?`} description="La demo pubblica va offline. Potrai ripristinare il progetto per 30 giorni da Backup e sicurezza → Cestino." onConfirm={onDelete} />
    </AppShell>
  );
}
